import * as THREE from 'three';
import { PLAYER } from '../game/constants';
import { ATTACKS, ATTACK_SEQUENCE, type AttackStyle, type HitReaction } from '../game/combat';
import { WEAPON_THROW, weaponSwingTiming, isHeavyWeapon, GREATSWORD_SPEED_FACTOR, type WeaponType } from '../game/weapons';
import { buildInjuries } from '../assets/CharacterInjuries';
import { createSeededRandom } from '../utils/random';
import {
  applyPose,
  buildCharacter,
  createPose,
  poseBomb,
  poseCatch,
  poseCelebrate,
  poseCharge,
  poseCombat,
  poseDive,
  poseDown,
  poseHammer,
  poseIdle,
  poseKick,
  posePass,
  poseGetup,
  poseReady,
  poseRun,
  poseSlide,
  poseStun,
  poseThrow,
  poseWeaponHold,
  poseWeaponSwing,
  poseWeaponThrow,
  poseWeightTransfer,
  poseInjury,
  poseCrawl,
  poseUnconscious,
  poseStumble,
  POSE,
  type CharacterRig,
  type CharacterSpec,
  type Pose,
} from '../assets/CharacterBuilder';
import type { ControlIntent } from '../core/InputController';

// A full stride covers roughly five world units, for host and network replicas alike.
const STRIDE_RADIANS_PER_UNIT = 1.25;

export type PlayerState =
  | 'idle'
  | 'run'
  | 'charge'
  | 'kick'
  | 'pass'
  | 'punch'
  | 'weaponSwing'
  | 'weaponThrow'
  | 'hammer'
  | 'bomb'
  | 'slide'
  | 'hurt'
  | 'down'
  | 'unconscious'
  | 'stumble'
  | 'getup'
  | 'celebrate'
  | 'gkGuard'
  | 'gkDive'
  | 'gkCatch'
  | 'gkThrow';

export type ItemType = 'bomb' | 'hammer' | 'magnet';

export interface PlayerDef {
  id: string;
  name: string;
  team: 'A' | 'B';
  isGoalkeeper: boolean;
  spec: CharacterSpec;
}

export interface HitWindow {
  type: 'punch' | 'slide' | 'hammer' | 'weapon';
  dirX: number;
  dirZ: number;
}

export interface PlayerEvents {
  onKickRequest?: (player: PlayerEntity, direction: THREE.Vector3, charge: number, aimAtGoal?: boolean) => void;
  onPassRequest?: (player: PlayerEntity) => void;
  onItemUse?: (player: PlayerEntity) => void;
  onAttackStart?: (player: PlayerEntity) => void;
  onLanding?: (player: PlayerEntity) => void;
  onStumble?: (player: PlayerEntity) => void;
  onEliminated?: (player: PlayerEntity) => void;
  onSlideStart?: (player: PlayerEntity) => void;
  onWeaponSwingStart?: (player: PlayerEntity) => void;
  onWeaponThrowRequest?: (player: PlayerEntity) => void;
}

export interface PlayerNetworkState {
  id: string;
  position: number[];
  velocity: number[];
  facing: number[];
  state: PlayerState;
  stateTime: number;
  hp: number;
  maxHp: number;
  airborneY: number;
  eliminated: boolean;
  weaponType: WeaponType | null;
  item: ItemType | null;
  attackStyle: AttackStyle;
  hitReaction: HitReaction;
  diveRoll: -1 | 1;
  punchArm: 'L' | 'R';
  runPhase: number;
}

const STATE_DURATIONS: Partial<Record<PlayerState, number>> = {
  kick: 0.55,
  pass: 0.45,
  punch: 0.52,
  weaponThrow: WEAPON_THROW.duration,
  hammer: 0.62,
  bomb: 0.58,
  slide: PLAYER.slideDuration,
  hurt: 0.42,
  getup: PLAYER.getupDuration,
  gkDive: 0.6,
  gkThrow: 0.55,
};

export class PlayerEntity {
  readonly id: string;
  readonly name: string;
  readonly team: 'A' | 'B';
  readonly isGoalkeeper: boolean;
  readonly rig: CharacterRig;
  readonly position = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();
  readonly facing = new THREE.Vector3(0, 0, 1);

  state: PlayerState = 'idle';
  stateTime = 0;
  stamina: number = PLAYER.maxStamina;
  maxHp: number = PLAYER.maxHp;
  hp: number = PLAYER.maxHp;
  eliminated = false;
  private stumbleTimer = 8;
  private recoveryDelay = 0;
  private readonly injuries: ReturnType<typeof buildInjuries>;
  charging = false;
  charge = 0;
  item: ItemType | null = null;
  weaponType: WeaponType | null = null;
  private weaponActionType: WeaponType = 'pipe';
  itemTimer = 0;
  magnetTimer = 0;
  controlledBy: 'human0' | 'human1' | 'ai' = 'ai';
  intent: ControlIntent | null = null;
  aiTarget: THREE.Vector2 | null = null;
  aiSprint = false;
  downDuration: number = PLAYER.downDuration;
  punched = false;
  slideDirection = new THREE.Vector3();
  kickPayload: { direction: THREE.Vector3; charge: number; aimAtGoal?: boolean } | null = null;
  kickDelivered = false;
  diveRoll: -1 | 1 = 1;
  hitLanded = false;
  punchArm: 'L' | 'R' = 'R';
  attackStyle: AttackStyle = 'punch';
  hitReaction: HitReaction = 'launch';
  private attackIndex = 0;
  private landingPending = false;
  airborneY = 0;
  airborneVy = 0;
  events: PlayerEvents = {};
  lastTouchTime = -10;

  private readonly targetPose: Pose = createPose();
  private readonly locomotionPose: Pose = createPose();
  private readonly shotgunAim = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));
  private readonly expressionBrows: THREE.Object3D | undefined;
  private readonly expressionEyes: THREE.Object3D | undefined;
  private readonly expressionMouth: THREE.Object3D | undefined;
  private readonly secondaryHair: THREE.Object3D | undefined;
  private runPhase = 0;
  private leanForward = 0;
  private leanLateral = 0;
  private turnLean = 0;
  private landingAge = 1;
  private landingStrength = 0;
  punchCooldown = 0;
  slideCooldown = 0;
  private readonly ringMesh: THREE.Mesh;
  private readonly itemIcon = new THREE.Group();
  private itemIconType: ItemType | null = null;
  private readonly bombIcon: THREE.Group;
  private readonly hammerIcon: THREE.Group;
  private readonly magnetIcon: THREE.Group;

  constructor(readonly def: PlayerDef, ringColor: string, private readonly random = createSeededRandom(def.spec.id)) {
    this.id = def.id;
    this.name = def.name;
    this.team = def.team;
    this.isGoalkeeper = def.isGoalkeeper;
    this.rig = buildCharacter(def.spec);
    this.injuries = buildInjuries(this.rig, def.spec);
    this.expressionBrows = this.rig.head.getObjectByName('expression-brows');
    this.expressionEyes = this.rig.head.getObjectByName('expression-eyes');
    this.expressionMouth = this.rig.head.getObjectByName('expression-mouth');
    this.secondaryHair = this.rig.head.getObjectByName('secondary-hair');

    const ringMaterial = new THREE.MeshBasicMaterial({
      color: ringColor,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    });
    this.ringMesh = new THREE.Mesh(new THREE.RingGeometry(0.5, 0.62, 32), ringMaterial);
    this.ringMesh.rotation.x = -Math.PI / 2;
    this.ringMesh.position.y = 0.03;
    this.ringMesh.visible = false;
    this.rig.root.add(this.ringMesh);

    this.bombIcon = this.buildIcon('bomb');
    this.hammerIcon = this.buildIcon('hammer');
    this.magnetIcon = this.buildIcon('magnet');
    this.itemIcon.add(this.bombIcon, this.hammerIcon, this.magnetIcon);
    this.itemIcon.position.y = 1.86;
    this.itemIcon.visible = false;
    this.rig.root.add(this.itemIcon);

  }

  private buildIcon(type: ItemType): THREE.Group {
    const group = new THREE.Group();
    if (type === 'bomb') {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(0.11, 12, 10),
        new THREE.MeshStandardMaterial({ color: '#22242c', roughness: 0.4 }),
      );
      group.add(sphere);
      const fuse = new THREE.Mesh(
        new THREE.CylinderGeometry(0.012, 0.012, 0.14, 6),
        new THREE.MeshStandardMaterial({ color: '#c9a86b', roughness: 0.8 }),
      );
      fuse.position.set(0.05, 0.12, 0);
      fuse.rotation.z = -0.5;
      group.add(fuse);
    } else if (type === 'hammer') {
      const handle = new THREE.Mesh(
        new THREE.CylinderGeometry(0.02, 0.02, 0.26, 8),
        new THREE.MeshStandardMaterial({ color: '#8a5a2b', roughness: 0.8 }),
      );
      group.add(handle);
      const head = new THREE.Mesh(
        new THREE.BoxGeometry(0.2, 0.11, 0.11),
        new THREE.MeshStandardMaterial({ color: '#c2c8d2', roughness: 0.35, metalness: 0.7 }),
      );
      head.position.y = 0.15;
      group.add(head);
    } else {
      const shape = new THREE.Mesh(
        new THREE.TorusGeometry(0.09, 0.028, 8, 20, Math.PI * 1.5),
        new THREE.MeshStandardMaterial({ color: '#ff4d4d', roughness: 0.4 }),
      );
      shape.rotation.z = Math.PI * 0.25;
      group.add(shape);
    }
    group.visible = false;
    return group;
  }

  get group(): THREE.Group {
    return this.rig.root;
  }

  get nextAttackStyle(): AttackStyle {
    return ATTACK_SEQUENCE[this.attackIndex % ATTACK_SEQUENCE.length];
  }

  get speed(): number {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }

  get isDown(): boolean {
    return this.hp <= 0 || this.state === 'down' || this.state === 'unconscious' || this.state === 'stumble' || this.state === 'getup' || this.state === 'hurt';
  }

  get canAct(): boolean {
    return !this.isDown && this.state !== 'celebrate';
  }

  get isCrawling(): boolean {
    return !this.eliminated && this.hp > 0 && this.injurySeverity >= 0.85 && this.airborneY <= 0 &&
      (this.state === 'idle' || this.state === 'run' || this.state === 'gkGuard');
  }

  get injurySeverity(): number {
    return 1 - this.hp / this.maxHp;
  }

  get mobilityFactor(): number {
    const injury = this.injurySeverity >= 0.85 ? 0.22 : 1 - this.injurySeverity * 0.48;
    return injury * (isHeavyWeapon(this.weaponType) ? GREATSWORD_SPEED_FACTOR : 1);
  }

  reset(position: THREE.Vector3, facingX: number, restoreHealth = true): void {
    this.recoveryDelay = PLAYER.hpRegenDelay;
    if (restoreHealth || this.eliminated) this.hp = this.maxHp;
    this.eliminated = false;
    this.group.visible = true;
    this.injuries.setDamage(this.injurySeverity);
    this.stumbleTimer = 7 + this.random() * 6;
    this.position.set(position.x, 0, position.z);
    this.velocity.set(0, 0, 0);
    this.airborneY = 0;
    this.airborneVy = 0;
    this.landingPending = false;
    this.runPhase = 0;
    this.leanForward = this.leanLateral = 0;
    this.turnLean = 0;
    this.landingAge = 1;
    this.landingStrength = 0;
    this.attackIndex = 0;
    this.attackStyle = 'punch';
    this.hitReaction = 'launch';
    this.hitLanded = false;
    this.punched = false;
    this.clearCombatScale();
    this.rig.armR.tip.rotation.x = 0;
    this.rig.legL.tip.rotation.x = this.rig.legR.tip.rotation.x = 0;
    if (this.expressionBrows) {
      this.expressionBrows.rotation.set(0, 0, 0);
      this.expressionBrows.scale.setScalar(1);
    }
    if (this.expressionEyes) {
      this.expressionEyes.rotation.set(0, 0, 0);
      this.expressionEyes.scale.setScalar(1);
    }
    if (this.expressionMouth) {
      this.expressionMouth.rotation.set(0, 0, 0);
      this.expressionMouth.scale.setScalar(1);
    }
    if (this.secondaryHair) this.secondaryHair.rotation.set(0, 0, 0);
    poseIdle(this.targetPose, 0);
    applyPose(this.rig, this.targetPose, 1000, 1);
    this.facing.set(facingX, 0, 0).normalize();
    this.state = this.hp <= 0 ? 'unconscious' : this.isGoalkeeper ? 'gkGuard' : 'idle';
    this.stateTime = 0;
    this.stamina = PLAYER.maxStamina;
    this.charging = false;
    this.charge = 0;
    this.item = null;
    this.weaponType = null;
    this.kickDelivered = false;
    this.itemTimer = 0;
    this.magnetTimer = 0;
    this.aiTarget = null;
    this.punchCooldown = 0;
    this.slideCooldown = 0;
    this.rig.root.rotation.y = Math.atan2(this.facing.x, this.facing.z);
    this.syncTransform();
  }

  setRingVisible(visible: boolean, color?: string): void {
    this.ringMesh.visible = visible;
    if (color) (this.ringMesh.material as THREE.MeshBasicMaterial).color.set(color);
  }

  setItem(type: ItemType | null, duration = 0): void {
    this.item = type;
    this.itemTimer = duration;
    this.itemIcon.visible = type !== null;
    this.bombIcon.visible = type === 'bomb';
    this.hammerIcon.visible = type === 'hammer';
    this.magnetIcon.visible = type === 'magnet';
    if (!type) this.itemIconType = null;
    else this.itemIconType = type;
  }

  get itemIconTypeUsed(): ItemType | null {
    return this.itemIconType;
  }

  startDown(duration: number, dir: THREE.Vector3 | null, force: number, air = false, reaction: HitReaction = 'launch', lift = 7.4): void {
    if (this.eliminated || this.state === 'unconscious') return;
    this.state = 'down';
    this.hitReaction = reaction;
    this.landingPending = air;
    this.stateTime = 0;
    this.downDuration = duration;
    this.charging = false;
    this.charge = 0;
    this.aiTarget = null;
    if (dir) {
      this.velocity.set(dir.x * force, 0, dir.z * force);
    }
    if (air) {
      this.airborneVy = lift;
      this.airborneY = Math.max(this.airborneY, 0.01);
    }
  }

  startHurt(dir: THREE.Vector3, force: number): void {
    if (this.hp <= 0) { this.startDown(0.5, dir, force); return; }
    this.state = 'hurt';
    this.stateTime = 0;
    this.charging = false;
    this.charge = 0;
    this.velocity.set(dir.x * force, 0, dir.z * force);
  }

  cancelCharge(): void {
    this.charging = false;
    this.charge = 0;
  }

  forceKick(direction: THREE.Vector3, charge: number, aimAtGoal = false): void {
    if (!this.canAct) return;
    this.cancelCharge();
    this.state = 'kick';
    this.stateTime = 0;
    this.kickPayload = { direction: direction.clone().setY(0).normalize(), charge, aimAtGoal };
    this.kickDelivered = false;
  }

  startDive(dir: THREE.Vector3): void {
    if (this.state === 'gkDive' || this.isDown) return;
    const side = this.facing.z * dir.x - this.facing.x * dir.z;
    this.diveRoll = side >= 0 ? 1 : -1;
    this.state = 'gkDive';
    this.stateTime = 0;
    this.facing.copy(dir).setY(0).normalize();
    this.velocity.copy(this.facing).multiplyScalar(7.5 * this.mobilityFactor);
    this.airborneVy = 11.5 * (1 - this.injurySeverity * 0.2);
    this.airborneY = Math.max(this.airborneY, 0.01);
  }

  startCatchHold(): void {
    this.state = 'gkCatch';
    this.stateTime = 0;
    this.velocity.set(0, 0, 0);
  }

  startThrow(): void {
    this.state = 'gkThrow';
    this.stateTime = 0;
  }

  get hitWindow(): HitWindow | null {
    if (this.hitLanded) return null;
    const timing = weaponSwingTiming(this.weaponActionType);
    if (this.state === 'weaponSwing' && this.weaponType && this.weaponType !== 'shotgun' && this.weaponType !== 'bazooka' && this.stateTime >= timing.hitStart && this.stateTime < timing.hitEnd) {
      return { type: 'weapon', dirX: this.facing.x, dirZ: this.facing.z };
    }
    if (this.state === 'punch' && this.stateTime >= ATTACKS[this.attackStyle].hitStart && this.stateTime < ATTACKS[this.attackStyle].hitEnd) {
      return { type: 'punch', dirX: this.facing.x, dirZ: this.facing.z };
    }
    if (this.state === 'hammer' && this.stateTime > 0.42 && this.stateTime < 0.62) {
      return { type: 'hammer', dirX: this.facing.x, dirZ: this.facing.z };
    }
    if (this.state === 'slide' && this.stateTime < PLAYER.slideDuration * 0.85) {
      return { type: 'slide', dirX: this.slideDirection.x, dirZ: this.slideDirection.z };
    }
    return null;
  }

  update(delta: number, elapsed: number, canControl: boolean): void {
    if (this.eliminated) return;
    if (canControl) {
      const healingTime = Math.max(0, delta - this.recoveryDelay);
      this.recoveryDelay = Math.max(0, this.recoveryDelay - delta);
      this.restoreHp(PLAYER.hpRegenPerSecond * healingTime);
    }
    this.stateTime += delta;
    this.punchCooldown = Math.max(0, this.punchCooldown - delta);
    this.slideCooldown = Math.max(0, this.slideCooldown - delta);
    this.magnetTimer = Math.max(0, this.magnetTimer - delta);
    if (this.item && this.itemTimer > 0) {
      this.itemTimer -= delta;
      if (this.itemTimer <= 0) this.setItem(null);
    }
    if (this.charging) {
      this.charge = Math.min(1, this.charge + delta / PLAYER.chargeTime);
    }

    const intent = canControl ? this.intent : null;
    if (intent && this.canAct) {
      this.processIntent(intent);
    }

    const previousVelocityX = this.velocity.x;
    const previousVelocityZ = this.velocity.z;
    this.updateMovement(delta);
    this.updateWeightTransfer(delta, previousVelocityX, previousVelocityZ);
    this.updateStateMachine(elapsed);
    this.updateAirborne(delta);
    if (canControl && this.hp > 0 && this.hp <= PLAYER.stumbleHp && this.state === 'run' && this.speed > 0.8 && this.airborneY <= 0) {
      this.stumbleTimer -= delta;
      if (this.stumbleTimer <= 0) {
        this.stumbleTimer = 7 + this.random() * 6;
        this.state = 'stumble';
        this.stateTime = 0;
        this.cancelCharge();
        this.velocity.set(this.facing.x * 4.5, 0, this.facing.z * 4.5);
        this.airborneVy = 3.2;
        this.airborneY = 0.01;
        this.events.onStumble?.(this);
      }
    }
    this.updatePose(delta, elapsed);
    this.syncTransform(delta);
    this.injuries.update(elapsed, this.state === 'unconscious');
  }

  networkState(): PlayerNetworkState {
    return {
      id: this.id, position: this.position.toArray(), velocity: this.velocity.toArray(), facing: this.facing.toArray(),
      state: this.state, stateTime: this.stateTime, hp: this.hp, maxHp: this.maxHp, airborneY: this.airborneY,
      eliminated: this.eliminated, weaponType: this.weaponType, item: this.item, attackStyle: this.attackStyle,
      hitReaction: this.hitReaction, diveRoll: this.diveRoll, punchArm: this.punchArm, runPhase: this.runPhase,
    };
  }

  updateReplica(snapshot: PlayerNetworkState, delta: number, elapsed: number, age: number, snapPosition = false): void {
    const previousHp = this.hp;
    const previousVelocityX = this.velocity.x;
    const previousVelocityZ = this.velocity.z;
    if (snapPosition) this.position.fromArray(snapshot.position);
    else {
      this.position.x += (snapshot.position[0] + snapshot.velocity[0] * Math.min(age, 0.25) - this.position.x) * (1 - Math.exp(-30 * delta));
      this.position.z += (snapshot.position[2] + snapshot.velocity[2] * Math.min(age, 0.25) - this.position.z) * (1 - Math.exp(-30 * delta));
    }
    this.velocity.fromArray(snapshot.velocity);
    this.facing.fromArray(snapshot.facing);
    this.state = snapshot.state;
    this.stateTime = snapshot.stateTime + age;
    this.hp = snapshot.hp;
    this.maxHp = snapshot.maxHp;
    this.airborneY = snapshot.airborneY;
    this.eliminated = snapshot.eliminated;
    this.group.visible = !snapshot.eliminated;
    this.weaponType = snapshot.weaponType;
    this.weaponActionType = snapshot.weaponType ?? this.weaponActionType;
    this.setItem(snapshot.item);
    this.attackStyle = snapshot.attackStyle;
    this.hitReaction = snapshot.hitReaction;
    this.diveRoll = snapshot.diveRoll;
    this.punchArm = snapshot.punchArm;
    const locomotion = this.state === 'run' || this.state === 'idle' || this.state === 'gkGuard';
    this.runPhase = (snapshot.runPhase + (locomotion ? age * this.speed * STRIDE_RADIANS_PER_UNIT : 0)) % (Math.PI * 2);
    if (previousHp !== this.hp) this.injuries.setDamage(this.injurySeverity);
    if (this.eliminated) return;
    this.updateWeightTransfer(delta, previousVelocityX, previousVelocityZ);
    this.updatePose(delta, elapsed, false);
    this.syncTransform(delta);
    this.injuries.update(elapsed, this.state === 'unconscious');
  }

  private processIntent(intent: ControlIntent): void {
    if (this.state === 'gkCatch' || this.state === 'gkThrow' || this.state === 'gkDive') return;

    const hasBall = this.hasBallSelf;
    if (this.weaponType && (intent.punchPressed || intent.passPressed) && this.punchCooldown <= 0 && (this.state === 'idle' || this.state === 'run' || this.state === 'charge')) {
      this.weaponActionType = this.weaponType;
      this.state = intent.punchPressed ? 'weaponSwing' : 'weaponThrow';
      this.stateTime = 0;
      this.hitLanded = false;
      this.kickDelivered = false;
      this.punchArm = 'R';
      this.punchCooldown = Math.max(PLAYER.punchCooldown, this.state === 'weaponSwing' ? weaponSwingTiming(this.weaponActionType).duration : WEAPON_THROW.duration);
      if (intent.moveX !== 0 || intent.moveZ !== 0) this.facing.set(intent.moveX, 0, intent.moveZ).normalize();
      this.cancelCharge();
      if (this.state === 'weaponSwing') this.events.onWeaponSwingStart?.(this);
      return;
    }
    // Armed inputs cannot become bare punches or ball passes during recovery.
    if (this.weaponType && (intent.punchPressed || intent.passPressed)) return;
    if (intent.punchPressed && this.punchCooldown <= 0 && (this.state === 'idle' || this.state === 'run' || this.state === 'charge')) {
      this.attackStyle = ATTACK_SEQUENCE[this.attackIndex % ATTACK_SEQUENCE.length];
      this.attackIndex += 1;
      if (intent.moveX !== 0 || intent.moveZ !== 0) this.facing.set(intent.moveX, 0, intent.moveZ).normalize();
      this.punchArm = this.punchArm === 'R' ? 'L' : 'R';
      this.state = 'punch';
      this.stateTime = 0;
      this.hitLanded = false;
      this.punchCooldown = Math.max(PLAYER.punchCooldown, ATTACKS[this.attackStyle].duration);
      this.cancelCharge();
      this.events.onAttackStart?.(this);
    }

    if (intent.passPressed && hasBall && (this.state === 'idle' || this.state === 'run' || this.state === 'charge')) {
      this.state = 'pass';
      this.stateTime = 0;
      this.kickDelivered = false;
      this.cancelCharge();
    }

    if (this.state === 'idle' || this.state === 'run' || this.state === 'charge') {
      if (!hasBall && intent.shootHeld && this.slideCooldown <= 0 && this.stateTime > 0.1) {
        this.state = 'slide';
        this.stateTime = 0;
        this.hitLanded = false;
        this.slideCooldown = PLAYER.slideCooldown;
        this.slideDirection.copy(this.facing).setY(0).normalize();
        this.velocity.set(
          this.slideDirection.x * PLAYER.slideSpeed,
          0,
          this.slideDirection.z * PLAYER.slideSpeed,
        );
        this.events.onSlideStart?.(this);
      } else if (hasBall && (intent.shootPressed || intent.shootHeld)) {
        if (this.item) {
          if (this.item === 'hammer') {
            this.state = 'hammer';
            this.stateTime = 0;
            this.hitLanded = false;
            this.events.onItemUse?.(this);
          } else if (this.item === 'bomb') {
            this.state = 'bomb';
            this.stateTime = 0;
            this.hitLanded = true;
            this.events.onItemUse?.(this);
          } else {
            this.events.onItemUse?.(this);
          }
        } else {
          this.forceKick(this.intentDirection(), 1);
        }
      }
    }

  }

  private hasBallSelf = false;

  setHasBall(value: boolean): void {
    this.hasBallSelf = value;
  }

  private intentDirection(): THREE.Vector3 {
    const dir = new THREE.Vector3(0, 0, 0);
    if (this.intent) {
      dir.set(this.intent.moveX, 0, this.intent.moveZ);
    }
    if (dir.lengthSq() < 0.01) {
      dir.copy(this.facing);
    }
    return dir.normalize();
  }

  private updateMovement(delta: number): void {
    const state = this.state;
    const controllable =
      state === 'idle' || state === 'run' || state === 'charge' || state === 'getup' || state === 'gkGuard';
    if (!controllable) {
      if (state !== 'slide' && state !== 'down' && state !== 'hurt' && state !== 'gkDive') {
        this.velocity.multiplyScalar(Math.max(0, 1 - delta * 8));
      }
      if (state === 'slide') {
        this.velocity.multiplyScalar(Math.max(0, 1 - delta * 3.2));
      }
      if (state === 'hurt' || state === 'down') {
        this.velocity.multiplyScalar(Math.max(0, 1 - delta * 4.4));
      }
      if (state === 'gkDive') {
        this.velocity.multiplyScalar(Math.max(0, 1 - delta * 1.6));
      }
      this.integratePosition(delta);
      return;
    }

    let moveX = 0;
    let moveZ = 0;
    let sprint = false;
    if (this.intent) {
      moveX = this.intent.moveX;
      moveZ = this.intent.moveZ;
      sprint = this.intent.sprint;
    }
    if (this.aiTarget) {
      const dx = this.aiTarget.x - this.position.x;
      const dz = this.aiTarget.y - this.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist > 0.08) {
        moveX = dx / dist;
        moveZ = dz / dist;
      } else {
        moveX = 0;
        moveZ = 0;
      }
      sprint = this.aiSprint;
    }

    const moving = moveX !== 0 || moveZ !== 0;
    const tired = this.stamina < 22;
    let targetSpeed = sprint && !tired ? PLAYER.sprintSpeed : PLAYER.runSpeed;
    targetSpeed *= this.mobilityFactor;
    if (tired) targetSpeed *= PLAYER.tiredFactor;
    if (state === 'charge') targetSpeed *= 0.42;
    if (state === 'getup') targetSpeed *= 0.7;

    if (moving) {
      const dir = new THREE.Vector3(moveX, 0, moveZ).normalize();
      const accel = PLAYER.acceleration * (1 - this.injurySeverity * 0.3) * delta;
      this.velocity.x += dir.x * accel * targetSpeed * 0.14;
      this.velocity.z += dir.z * accel * targetSpeed * 0.14;
      const cap = targetSpeed;
      const speed = Math.hypot(this.velocity.x, this.velocity.z);
      if (speed > cap) {
        const k = cap / speed;
        this.velocity.x *= k;
        this.velocity.z *= k;
      }
    } else {
      this.velocity.multiplyScalar(Math.max(0, 1 - delta * 9));
    }
    const lookAtBall = this.isGoalkeeper && this.controlledBy === 'ai' && this.intent?.lookX !== undefined && this.intent.lookZ !== undefined;
    if (moving || lookAtBall) {
      const targetFacingX = lookAtBall ? this.intent!.lookX! : moveX;
      const targetFacingZ = lookAtBall ? this.intent!.lookZ! : moveZ;
      const currentAngle = Math.atan2(this.facing.x, this.facing.z);
      const targetAngle = Math.atan2(targetFacingX, targetFacingZ);
      let diff = targetAngle - currentAngle;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      const turn = Math.min(1, PLAYER.turnRate * (1 - this.injurySeverity * 0.2) * delta) * 0.55;
      const newAngle = currentAngle + diff * turn;
      this.facing.set(Math.sin(newAngle), 0, Math.cos(newAngle));
    }

    if ((this.state === 'idle' || this.state === 'gkGuard') && moving) this.state = 'run';
    if (this.state === 'run' && !moving) this.state = this.isGoalkeeper ? 'gkGuard' : 'idle';

    const draining = sprint && moving && !tired;
    this.stamina = Math.max(
      0,
      Math.min(PLAYER.maxStamina, this.stamina + (draining ? -PLAYER.sprintDrain : PLAYER.staminaRegen * (moving ? 0.55 : 1)) * delta),
    );
    this.integratePosition(delta);
  }

  private integratePosition(delta: number): void {
    this.position.x += this.velocity.x * delta;
    this.position.z += this.velocity.z * delta;
    const margin = PLAYER.radius + 0.05;
    const limitX = 14.56 - margin;
    const limitZ = 8.6 - margin;
    this.position.x = THREE.MathUtils.clamp(this.position.x, -limitX, limitX);
    this.position.z = THREE.MathUtils.clamp(this.position.z, -limitZ, limitZ);
  }

  private updateStateMachine(elapsed: number): void {
    const duration = this.state === 'punch' ? ATTACKS[this.attackStyle].duration
      : this.state === 'weaponSwing' ? weaponSwingTiming(this.weaponActionType).duration : STATE_DURATIONS[this.state];
    if (this.state === 'weaponThrow') {
      if (!this.kickDelivered && this.stateTime >= WEAPON_THROW.release) {
        this.kickDelivered = true;
        this.events.onWeaponThrowRequest?.(this);
      }
      if (this.stateTime >= WEAPON_THROW.duration) {
        this.state = this.isGoalkeeper ? 'gkGuard' : 'idle';
        this.stateTime = 0;
      }
    } else if (this.state === 'kick' && duration !== undefined) {
      if (!this.kickDelivered && this.stateTime > 0.3 && this.kickPayload) {
        this.kickDelivered = true;
        this.events.onKickRequest?.(this, this.kickPayload.direction, this.kickPayload.charge, this.kickPayload.aimAtGoal);
      }
      if (this.stateTime >= duration) {
        this.state = this.isGoalkeeper ? 'gkGuard' : 'idle';
        this.stateTime = 0;
      }
    } else if (this.state === 'pass') {
      if (!this.kickDelivered && this.stateTime > 0.27) {
        this.kickDelivered = true;
        this.events.onPassRequest?.(this);
      }
      if (this.stateTime >= 0.45) {
        this.state = this.isGoalkeeper ? 'gkGuard' : 'idle';
        this.stateTime = 0;
      }
    } else if (this.state === 'slide' && this.stateTime >= PLAYER.slideDuration + PLAYER.slideRecovery) {
      this.state = this.isGoalkeeper ? 'gkGuard' : 'idle';
      this.stateTime = 0;
    } else if (this.state === 'down') {
      if (this.hp <= 0 && this.airborneY <= 0 && this.stateTime >= 0.3) {
        this.clearCombatScale();
        this.state = 'unconscious';
        this.stateTime = 0;
        this.velocity.set(0, 0, 0);
      } else if (this.stateTime >= this.downDuration && this.airborneY <= 0) {
        this.clearCombatScale();
        this.state = 'getup';
        this.stateTime = 0;
        this.stamina = Math.max(this.stamina, PLAYER.reviveStamina * 0.4);
        this.punched = false;
      }
    } else if (this.state === 'unconscious') {
      if (this.stateTime >= PLAYER.knockoutDuration) {
        this.hp = Math.min(this.maxHp, PLAYER.reviveHp);
        this.injuries.setDamage(this.injurySeverity);
        this.stamina = PLAYER.reviveStamina;
        this.stumbleTimer = 7 + this.random() * 6;
        this.state = 'getup';
        this.stateTime = 0;
        this.punched = false;
      }
    } else if (this.state === 'stumble') {
      if (this.stateTime >= 1.35 && this.airborneY <= 0) {
        this.state = 'getup';
        this.stateTime = 0;
      }
    } else if (this.state === 'getup') {
      if (this.stateTime >= PLAYER.getupDuration) {
        this.state = this.isGoalkeeper ? 'gkGuard' : 'idle';
        this.stateTime = 0;
      }
    } else if (this.state === 'gkDive') {
      if (this.stateTime >= 0.6 || this.airborneY <= 0.01) {
        this.state = 'gkGuard';
        this.stateTime = 0;
      }
    } else if (this.state === 'gkCatch') {
      if (this.stateTime >= 1.05) {
        this.state = 'gkThrow';
        this.stateTime = 0;
      }
    } else if (this.state === 'gkThrow') {
      if (this.stateTime >= 0.55) {
        this.state = 'gkGuard';
        this.stateTime = 0;
      }
    } else if (
      (this.state === 'punch' || this.state === 'hammer' || this.state === 'bomb' || this.state === 'weaponSwing') &&
      duration !== undefined &&
      this.stateTime >= duration
    ) {
      this.state = this.isGoalkeeper ? 'gkGuard' : 'idle';
      this.stateTime = 0;
    } else if (this.state === 'hurt' && this.stateTime >= 0.42) {
      this.state = this.isGoalkeeper ? 'gkGuard' : 'idle';
      this.stateTime = 0;
    } else if (this.state === 'celebrate') {
      const drift = Math.atan2(this.facing.x, this.facing.z) + Math.sin(elapsed * 3 + this.position.x) * 0.004;
      this.facing.set(Math.sin(drift), 0, Math.cos(drift));
    }
  }

  private updateAirborne(delta: number): void {
    this.landingAge = Math.min(1, this.landingAge + delta);
    if (this.airborneY > 0 || this.airborneVy > 0) {
      this.airborneVy -= 40 * delta;
      this.airborneY += this.airborneVy * delta;
      if (this.airborneY <= 0) {
        this.landingAge = 0;
        this.landingStrength = Math.min(1, Math.abs(this.airborneVy) / 18);
        this.airborneY = 0;
        this.airborneVy = 0;
        if (this.landingPending) {
          this.landingPending = false;
          this.events.onLanding?.(this);
        }
      }
    }
  }

  private updateWeightTransfer(delta: number, previousX: number, previousZ: number): void {
    const locomotion = this.state === 'run' || this.state === 'idle' || this.state === 'gkGuard';
    const accelerationX = delta > 0 && locomotion ? (this.velocity.x - previousX) / delta : 0;
    const accelerationZ = delta > 0 && locomotion ? (this.velocity.z - previousZ) / delta : 0;
    const forward = THREE.MathUtils.clamp((accelerationX * this.facing.x + accelerationZ * this.facing.z) / 36, -1, 1);
    const lateral = THREE.MathUtils.clamp((accelerationX * this.facing.z - accelerationZ * this.facing.x) / 36, -1, 1);
    const blend = 1 - Math.exp(-12 * delta);
    this.leanForward += (forward - this.leanForward) * blend;
    this.leanLateral += (lateral - this.leanLateral) * blend;
  }

  private updatePose(delta: number, elapsed: number, advanceStride = true): void {
    // Full turns are equivalent to zero; remove them before blending into a resting pose.
    if (this.state !== 'punch' && (this.state !== 'down' || this.airborneY <= 0)) {
      const rotation = this.rig.hips.rotation;
      rotation.set(
        Math.atan2(Math.sin(rotation.x), Math.cos(rotation.x)),
        Math.atan2(Math.sin(rotation.y), Math.cos(rotation.y)),
        Math.atan2(Math.sin(rotation.z), Math.cos(rotation.z)),
      );
    }
    const pose = this.targetPose;
    const speed01 = THREE.MathUtils.clamp(this.speed / PLAYER.sprintSpeed, 0, 1);
    const locomotion = this.state === 'run' || this.state === 'idle' || this.state === 'gkGuard';
    if (locomotion && advanceStride) this.runPhase = (this.runPhase + delta * this.speed * STRIDE_RADIANS_PER_UNIT) % (Math.PI * 2);
    const personalityTime = elapsed + this.def.spec.id * 0.73;
    switch (this.state) {
      case 'idle':
        poseIdle(pose, personalityTime);
        break;
      case 'run': {
        // Speed-matched cadence keeps feet from churning when nearly stopped.
        const speed = this.speed;
        const forward = speed > 0.01 ? (this.velocity.x * this.facing.x + this.velocity.z * this.facing.z) / speed : 1;
        const lateral = speed > 0.01 ? (this.velocity.x * this.facing.z - this.velocity.z * this.facing.x) / speed : 0;
        poseRun(pose, this.runPhase, speed01, forward, lateral);
        break;
      }
      case 'charge':
        poseCharge(pose, this.charge, elapsed);
        break;
      case 'kick':
        poseKick(pose, this.stateTime / 0.55);
        break;
      case 'pass':
        posePass(pose, this.stateTime / 0.45);
        break;
      case 'punch':
        poseCombat(pose, this.stateTime / ATTACKS[this.attackStyle].duration, this.attackStyle, this.punchArm);
        break;
      case 'weaponSwing':
        poseWeaponSwing(pose, this.stateTime / weaponSwingTiming(this.weaponActionType).duration, this.weaponActionType);
        break;
      case 'weaponThrow':
        poseWeaponThrow(pose, this.stateTime / WEAPON_THROW.duration);
        break;
      case 'hammer':
        poseHammer(pose, this.stateTime / 0.62);
        break;
      case 'bomb':
        poseBomb(pose, this.stateTime / 0.58);
        break;
      case 'slide':
        poseSlide(pose, this.stateTime / PLAYER.slideDuration);
        break;
      case 'hurt':
        poseStun(pose, elapsed * 2.2);
        break;
      case 'down':
        poseDown(pose, this.stateTime / this.downDuration);
        break;
      case 'unconscious':
        // The hit already knocked the body down; do not stand it up for a second fall.
        poseUnconscious(pose, Math.max(0.2, this.stateTime / PLAYER.knockoutDuration), elapsed);
        break;
      case 'stumble':
        poseStumble(pose, this.stateTime / 1.35, this.def.spec.id % 2 ? 1 : -1);
        break;
      case 'getup':
        poseGetup(pose, this.stateTime / PLAYER.getupDuration, elapsed);
        break;
      case 'celebrate':
        poseCelebrate(pose, elapsed);
        break;
      case 'gkGuard':
        poseReady(pose, personalityTime);
        break;
      case 'gkDive':
        poseDive(pose, this.diveRoll, this.stateTime / 0.6);
        break;
      case 'gkCatch':
        poseCatch(pose, this.stateTime);
        break;
      case 'gkThrow':
        poseThrow(pose, this.stateTime / 0.55);
        break;
    }
    const landing = locomotion && this.landingAge < 0.45
      ? this.landingStrength * Math.cos(this.landingAge * 22) * Math.exp(-this.landingAge * 13)
      : 0;
    if (locomotion) {
      // Keep taking shorter steps while decelerating instead of sliding in an idle stance.
      if (this.state !== 'run' && speed01 > 0.02) {
        const speed = this.speed;
        const forward = (this.velocity.x * this.facing.x + this.velocity.z * this.facing.z) / speed;
        const lateral = (this.velocity.x * this.facing.z - this.velocity.z * this.facing.x) / speed;
        poseRun(this.locomotionPose, this.runPhase, speed01, forward, lateral);
        const moving = THREE.MathUtils.smoothstep(speed01, 0.02, 0.3);
        for (let i = 0; i < pose.length; i++) pose[i] += (this.locomotionPose[i] - pose[i]) * moving;
      }
      poseWeightTransfer(pose, this.leanForward, this.leanLateral, landing);
    }
    const facingDelta = Math.atan2(this.facing.x, this.facing.z) - this.rig.root.rotation.y;
    const turn = Math.atan2(Math.sin(facingDelta), Math.cos(facingDelta));
    const bank = locomotion ? THREE.MathUtils.clamp(turn, -0.65, 0.65) * speed01 : 0;
    this.turnLean += (bank - this.turnLean) * (1 - Math.exp(-14 * delta));
    if (locomotion && this.weaponType !== 'shotgun' && this.weaponType !== 'bazooka') {
      // The feet turn first, the torso banks, and the eyes lead into the new direction.
      pose[POSE.hipsRoll] -= this.turnLean * 0.16;
      pose[POSE.torsoRoll] -= this.turnLean * 0.24;
      pose[POSE.headYaw] += this.turnLean * 0.3;
    }
    if (locomotion) poseInjury(pose, this.injurySeverity, this.runPhase, elapsed + this.def.spec.id * 0.73, this.speed > 0.2, this.def.spec.id % 2 ? 1 : -1);
    const crawling = this.isCrawling;
    if (crawling) poseCrawl(pose, this.runPhase, elapsed, this.speed > 0.2);
    if (!crawling && this.weaponType && (this.state === 'idle' || this.state === 'run' || this.state === 'charge')) poseWeaponHold(pose, this.weaponType);
    const armedAction = this.state === 'weaponSwing' || this.state === 'weaponThrow';
    const impact = this.state === 'hurt' || this.state === 'gkDive' || this.state === 'gkCatch';
    // Slide contact starts immediately; show the launched pose before contact hitstop freezes it.
    const smoothing = this.state === 'slide' && this.stateTime <= delta ? 1000
      : this.state === 'punch' || this.state === 'kick' || armedAction ? 30 : impact ? 24 : 16;
    applyPose(this.rig, pose, smoothing, delta);
    const ankleBlend = 1 - Math.exp(-20 * delta);
    const footSpeed = locomotion && !crawling ? speed01 : 0;
    // Plant the heel, roll over the sole, then lift the toe during the swing.
    const leftStep = Math.sin(this.runPhase);
    const rightStep = -leftStep;
    const leftAnkle = (leftStep * 0.28 - Math.max(0, Math.cos(this.runPhase)) * 0.22) * footSpeed;
    const rightAnkle = (rightStep * 0.28 - Math.max(0, -Math.cos(this.runPhase)) * 0.22) * footSpeed;
    this.rig.legL.tip.rotation.x += (leftAnkle - this.rig.legL.tip.rotation.x) * ankleBlend;
    this.rig.legR.tip.rotation.x += (rightAnkle - this.rig.legR.tip.rotation.x) * ankleBlend;
    // Keep the prop's +Y axis above the grip rather than back through the forearm.
    const hand = this.rig.armR.tip;
    const holding = this.weaponType !== null || armedAction;
    const swing = weaponSwingTiming(this.weaponActionType);
    const contact = this.state === 'weaponSwing' ? THREE.MathUtils.smoothstep(this.stateTime, swing.hitStart - 0.12, swing.hitStart) : 0;
    const release = this.state === 'weaponThrow' ? THREE.MathUtils.smoothstep(this.stateTime, 0.12, WEAPON_THROW.release) : 0;
    const rangedWeapon = this.weaponType === 'shotgun' || this.weaponType === 'bazooka';
    const tilt = rangedWeapon || armedAction && (this.weaponActionType === 'shotgun' || this.weaponActionType === 'bazooka')
      ? Math.PI / 2
      : 0.35 + contact * (this.weaponActionType === 'pipe' ? 1.0 : 1.8) + release * 1.1;
    const desired = holding ? pose[POSE.armRPitch] + pose[POSE.elbowR] + tilt : 0;
    if (rangedWeapon && this.state !== 'weaponThrow' && !this.isDown && !crawling) {
      // Cancel both arm joints so the two barrels stay level with the chest.
      hand.quaternion.copy(this.rig.armR.upper.quaternion).multiply(this.rig.armR.lower.quaternion).invert().multiply(this.shotgunAim);
    } else {
      hand.rotation.set(hand.rotation.x + (desired - hand.rotation.x) * (1 - Math.exp(-smoothing * delta)), 0, 0);
    }
    this.updateCombatScale();
    if (landing !== 0) {
      const squash = 1 - landing * 0.12;
      const width = 1 / Math.sqrt(squash);
      this.rig.hips.scale.x *= width;
      this.rig.hips.scale.y *= squash;
      this.rig.hips.scale.z *= width;
    }
    this.updateExpression(delta, elapsed);
  }

  private updateExpression(delta: number, elapsed: number): void {
    const blend = 1 - Math.exp(-18 * delta);
    const unconscious = this.state === 'unconscious';
    const hurt = this.state === 'hurt' || this.state === 'down';
    const injury = this.injurySeverity;
    const pain = injury * Math.max(0, Math.sin(elapsed * 2.6 + this.def.spec.id)) ** 4;
    const attack = this.state === 'punch' || this.state === 'weaponSwing' || this.state === 'weaponThrow'
      || this.state === 'kick' || this.state === 'pass' || this.state === 'hammer' || this.state === 'bomb' || this.state === 'gkThrow';
    const excited = this.state === 'celebrate';
    const effort = this.state === 'charge' ? this.charge : attack ? 0.7 + Math.sin(Math.min(1, this.stateTime / 0.3) * Math.PI) * 0.3 : 0;
    const running = this.state === 'run' ? THREE.MathUtils.clamp(this.speed / PLAYER.sprintSpeed, 0, 1) : 0;
    const breath = Math.max(0, Math.sin(elapsed * 8.5 + this.def.spec.id)) * running * (0.12 + injury * 0.2);
    const blinkPeriod = 3.7 + this.def.spec.id % 5 * 0.19;
    const blinkPhase = (elapsed + this.def.spec.id * 0.73) % blinkPeriod;
    // Keep the eyes focused through a strike instead of blinking over its contact frame.
    const blink = !attack && blinkPhase > blinkPeriod - 0.16 ? Math.sin((blinkPhase - blinkPeriod + 0.16) / 0.16 * Math.PI) : 0;
    if (this.expressionBrows) {
      const target = hurt ? 0.22 : excited ? -0.18 : -0.13 * effort + injury * 0.18;
      this.expressionBrows.rotation.z += (target - this.expressionBrows.rotation.z) * blend;
      this.expressionBrows.scale.y += ((hurt ? 1.35 : 1 + effort * 0.4 + pain * 0.6) - this.expressionBrows.scale.y) * blend;
    }
    if (this.expressionEyes) {
      const wide = unconscious ? 0.08 : hurt ? 1.5 : excited ? 0.8 : 1 - effort * 0.3 - injury * 0.6 - pain * 0.4;
      const target = Math.max(0.08, wide * (1 - blink));
      this.expressionEyes.scale.y += (target - this.expressionEyes.scale.y) * blend;
      this.expressionEyes.rotation.z += ((hurt ? Math.sin(elapsed * 12) * 0.12 : 0) - this.expressionEyes.rotation.z) * blend;
    }
    if (this.expressionMouth) {
      const shout = unconscious ? 0.5 : hurt ? 2.8 : excited ? 1.65 + Math.max(0, Math.sin(elapsed * 9)) * 0.5 : attack ? 1.15 + effort * 0.6 : 1 - effort * 0.45 + injury * 0.5 + pain * 1.8 + breath;
      this.expressionMouth.scale.y += (shout - this.expressionMouth.scale.y) * blend;
      this.expressionMouth.scale.x += ((hurt ? 0.7 : excited ? 1.25 : 1) - this.expressionMouth.scale.x) * blend;
      this.expressionMouth.rotation.z += ((hurt ? Math.sin(elapsed * 14) * 0.18 : 0) - this.expressionMouth.rotation.z) * blend;
    }
    if (this.secondaryHair) {
      const lag = -this.targetPose[POSE.torsoPitch] * 0.16 + Math.sin(this.runPhase * 2 - 0.8) * running * 0.22 - this.leanForward * 0.08;
      this.secondaryHair.rotation.x += (lag - this.secondaryHair.rotation.x) * (1 - Math.exp(-9 * delta));
      const wobble = hurt ? Math.sin(elapsed * 15) * 0.25 : Math.sin(elapsed * 4 + this.def.spec.id) * 0.035 + Math.sin(this.runPhase - 0.6) * running * 0.1 - this.turnLean * 0.22;
      this.secondaryHair.rotation.z += (wobble - this.secondaryHair.rotation.z) * blend;
    }
  }

  private clearCombatScale(): void {
    this.rig.hips.scale.setScalar(1);
    this.rig.armL.tip.scale.set(1, 0.92, this.isGoalkeeper ? 0.82 : 0.95);
    this.rig.armR.tip.scale.copy(this.rig.armL.tip.scale);
    this.rig.legL.tip.scale.setScalar(1);
    this.rig.legR.tip.scale.setScalar(1);
  }

  private updateCombatScale(): void {
    this.clearCombatScale();
    if (this.state === 'run') {
      const bounce = Math.cos(this.runPhase * 2) * 0.07 * THREE.MathUtils.clamp(this.speed / PLAYER.sprintSpeed, 0, 1);
      const width = 1 / Math.sqrt(1 + bounce);
      this.rig.hips.scale.set(width, 1 + bounce, width);
    } else if (this.state === 'charge') {
      const squash = 1 - this.charge * 0.16;
      this.rig.hips.scale.set(1 / Math.sqrt(squash), squash, 1 / Math.sqrt(squash));
    } else if (this.state === 'celebrate') {
      const stretch = 1 + Math.sin(this.stateTime * 12) * 0.1;
      this.rig.hips.scale.set(1 / Math.sqrt(stretch), stretch, 1 / Math.sqrt(stretch));
    } else if (this.state === 'punch') {
      const attack = ATTACKS[this.attackStyle];
      const t = this.stateTime / attack.duration;
      const swell = THREE.MathUtils.smoothstep(t, 0.04, 0.27) * (1 - THREE.MathUtils.smoothstep(t, 0.68, 1));
      if (!this.weaponType && (this.attackStyle === 'punch' || this.attackStyle === 'uppercut')) {
        this.rig[this.punchArm === 'L' ? 'armL' : 'armR'].tip.scale.multiplyScalar(1 + 3.5 * swell);
      } else if (this.attackStyle === 'kick') {
        this.rig.legL.tip.scale.setScalar(1 + 2.1 * swell);
        this.rig.legR.tip.scale.setScalar(1 + 2.6 * swell);
      } else if (this.attackStyle === 'spin') {
        this.rig.legR.tip.scale.setScalar(1 + 3.8 * swell);
      }
      const squash = 1 - 0.25 * Math.sin(Math.min(1, t / 0.35) * Math.PI);
      this.rig.hips.scale.set(1 / Math.sqrt(squash), squash, 1 / Math.sqrt(squash));
    } else if (this.state === 'down') {
      if (this.airborneY > 0.02) {
        const spin = this.stateTime * (this.hitReaction === 'spin' ? 23 : 10);
        this.rig.hips.rotation.x = spin;
        this.rig.hips.rotation.z = this.hitReaction === 'spin' ? spin * 0.7 : Math.sin(spin) * 0.4;
        this.rig.hips.scale.set(0.8, 1.5, 0.8);
      } else if (this.hitReaction === 'flatten') {
        this.rig.hips.scale.set(1.9, 0.2, 1.9);
        this.rig.hips.position.y = 0.18;
        this.rig.hips.rotation.set(0, 0, 0);
      } else {
        const wobble = Math.sin(this.stateTime * 26) * Math.exp(-this.stateTime * 4);
        this.rig.hips.scale.set(1.25 + wobble, 0.65, 1.25 - wobble);
      }
    }
  }

  private syncTransform(delta = 1 / 60): void {
    this.rig.root.position.set(this.position.x, this.airborneY, this.position.z);
    const angle = Math.atan2(this.facing.x, this.facing.z);
    let diff = angle - this.rig.root.rotation.y;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    // Same response at 30, 60 or 120 Hz; 25.85 preserves the old 60 Hz feel.
    this.rig.root.rotation.y += diff * (1 - Math.exp(-25.85 * delta));
  }

  applyDamage(amount: number, hitDowned = false, attacker?: PlayerEntity): boolean {
    if (!Number.isFinite(amount) || amount <= 0 || this.eliminated || this.isDown && !hitDowned) return false;
    if (this.isCrawling) {
      return this.eliminate(attacker);
    }
    const previousHp = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    this.recoveryDelay = PLAYER.hpRegenDelay;
    this.injuries.setDamage(this.injurySeverity);
    this.stamina = Math.max(0, this.stamina - amount * PLAYER.hitStaminaScale);
    if (this.hp <= 0) this.startDown(0.5, null, 0);
    if (this.hp < previousHp) this.creditEffectiveHit(attacker);
    return this.hp <= 0;
  }

  eliminate(attacker?: PlayerEntity): boolean {
    if (this.eliminated) return false;
    this.eliminated = true;
    this.hp = 0;
    this.state = 'unconscious';
    this.stateTime = 0;
    this.velocity.set(0, 0, 0);
    this.airborneY = this.airborneVy = 0;
    this.cancelCharge();
    this.aiTarget = null;
    this.group.visible = false;
    this.events.onEliminated?.(this);
    this.setItem(null);
    this.creditEffectiveHit(attacker);
    return true;
  }

  private creditEffectiveHit(attacker?: PlayerEntity): void {
    if (attacker && attacker.team !== this.team) attacker.restoreHp(PLAYER.hpOnHit);
  }

  restoreHp(amount: number): number {
    if (!Number.isFinite(amount) || amount <= 0 || this.eliminated || this.hp <= 0 || this.hp >= this.maxHp) return 0;
    const previous = this.hp;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    this.injuries.setDamage(this.injurySeverity);
    return this.hp - previous;
  }

  setMaxHp(value: number): void {
    const next = Number.isFinite(value) ? Math.max(1, Math.min(1000, Math.round(value))) : PLAYER.maxHp;
    this.hp = Math.max(0, Math.min(next, this.hp / this.maxHp * next));
    this.maxHp = next;
    this.injuries.setDamage(this.injurySeverity);
  }

  heal(amount: number): void {
    this.stamina = Math.min(PLAYER.maxStamina, this.stamina + amount);
  }

  dispose(): void {
    this.rig.root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      mesh.geometry?.dispose?.();
      const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
      for (const material of materials) material.dispose();
    });
  }
}

export function magnetActive(player: PlayerEntity): boolean {
  return player.magnetTimer > 0;
}
